import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DbAttendance, AttendanceUpdate } from "@/types/rota";
import { useToast } from "@/hooks/use-toast";
import { logAudit } from "@/lib/audit";
import { useNurseryId } from "@/hooks/useNurseryId";
import { friendlyError } from "@/lib/friendlyError";



export function useAttendance(date: string) {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["attendance", nurseryId, date],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance")
        .select("*")
        .eq("date", date);
      if (error) throw error;
      return data as DbAttendance[];
    },
  });
}

async function fetchNurseryId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data: profile } = await supabase
    .from("profiles")
    .select("nursery_id")
    .eq("user_id", user.id)
    .single();
  if (!profile?.nursery_id) throw new Error("No nursery found");
  return profile.nursery_id;
}

/**
 * Safeguarding record of who handed the child over. Written before the sign-in or
 * sign-out itself, because the database refuses attendance without it.
 */
async function recordHandover(args: {
  nurseryId: string;
  childId: string;
  date: string;
  direction: "in" | "out";
  relationship: string;
  personName: string;
  userId?: string;
  userName?: string;
}) {
  const { error } = await supabase.from("attendance_handovers").insert({
    nursery_id: args.nurseryId,
    child_id: args.childId,
    date: args.date,
    direction: args.direction,
    relationship_type: args.relationship,
    person_name: args.personName.trim(),
    source: "register",
    recorded_by: args.userId || null,
    recorded_by_name: args.userName || null,
  } as any);
  if (error) throw error;
}

type HandoverArgs = { relationship: string; personName: string; userName?: string };

export function useCheckIn() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ childId, date, userId, relationship, personName, userName }: { childId: string; date: string; userId: string } & HandoverArgs) => {
      const nurseryId = await fetchNurseryId();
      await recordHandover({ nurseryId, childId, date, direction: "in", relationship, personName, userId, userName });
      const { data, error } = await supabase
        .from("attendance")
        .upsert(
          {
            child_id: childId,
            date,
            status: "present",
            check_in_time: new Date().toISOString(),
            checked_in_by: userId,
            nursery_id: nurseryId,
          } as any,
          { onConflict: "child_id,date" }
        )
        .select()
        .single();
      if (error) throw error;
      return data as DbAttendance;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-handovers"] });
      toast({ title: "Checked in ✅" });
      logAudit({ description: `Checked in child for ${data.date}`, recordType: "attendance", recordId: data.child_id });
    },
    onError: (e: Error) => {
      toast({ title: "Could not sign that child in", description: friendlyError(e), variant: "destructive" });
    },
  });
}

export function useCheckOut() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ childId, date, userId, relationship, personName, userName }: { childId: string; date: string; userId: string } & HandoverArgs) => {
      const nurseryId = await fetchNurseryId();
      await recordHandover({ nurseryId, childId, date, direction: "out", relationship, personName, userId, userName });
      const { data, error } = await supabase
        .from("attendance")
        .update({
          check_out_time: new Date().toISOString(),
          checked_out_by: userId,
        })
        .eq("child_id", childId)
        .eq("date", date)
        .select()
        .single();
      if (error) throw error;
      return data as DbAttendance;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-handovers"] });
      toast({ title: "Checked out 👋" });
      logAudit({ description: `Checked out child for ${data.date}`, recordType: "attendance", recordId: data.child_id });
    },
    onError: (e: Error) => {
      toast({ title: "Could not sign that child out", description: friendlyError(e), variant: "destructive" });
    },
  });
}


export function useMarkAbsent() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ childId, date, notes }: { childId: string; date: string; notes?: string }) => {
      const nurseryId = await fetchNurseryId();
      const { data, error } = await supabase
        .from("attendance")
        .upsert(
          {
            child_id: childId,
            date,
            status: "absent",
            notes: notes || "",
            nursery_id: nurseryId,
          } as any,
          { onConflict: "child_id,date" }
        )
        .select()
        .single();
      if (error) throw error;
      return data as DbAttendance;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      toast({ title: "Marked absent" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not save that", description: friendlyError(e), variant: "destructive" });
    },
  });
}

export function useUndoAttendance() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ childId, date }: { childId: string; date: string }) => {
      const { error } = await supabase
        .from("attendance")
        .delete()
        .eq("child_id", childId)
        .eq("date", date);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      toast({ title: "Undone ↩" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not undo that", description: friendlyError(e), variant: "destructive" });
    },
  });
}

export function useUpdateAbsenceReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ childId, date, absenceReason, absenceCategory, notes }: {
      childId: string; date: string; absenceReason: string; absenceCategory?: string; notes?: string;
    }) => {
      const update: Record<string, string> = { absence_reason: absenceReason };
      if (absenceCategory !== undefined) update.absence_category = absenceCategory;
      if (notes !== undefined) update.notes = notes;
      const { data, error } = await supabase
        .from("attendance")
        .update(update as any)
        .eq("child_id", childId)
        .eq("date", date)
        .select()
        .single();
      if (error) throw error;
      return data as DbAttendance;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
  });
}

/**
 * Signing several children in at once still needs the person who dropped each one
 * off, so this takes an entry per child rather than a list of ids.
 */
export function useBulkCheckIn() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ entries, date, userId, userName }: {
      entries: { childId: string; relationship: string; personName: string }[];
      date: string;
      userId: string;
      userName?: string;
    }) => {
      const nurseryId = await fetchNurseryId();
      const now = new Date().toISOString();

      const { error: handoverError } = await supabase.from("attendance_handovers").insert(
        entries.map((e) => ({
          nursery_id: nurseryId,
          child_id: e.childId,
          date,
          direction: "in",
          relationship_type: e.relationship,
          person_name: e.personName.trim(),
          source: "register",
          recorded_by: userId || null,
          recorded_by_name: userName || null,
        })) as any
      );
      if (handoverError) throw handoverError;

      const records = entries.map((e) => ({
        child_id: e.childId,
        date,
        status: "present",
        check_in_time: now,
        checked_in_by: userId,
        nursery_id: nurseryId,
      }));
      const { error } = await supabase
        .from("attendance")
        .upsert(records as any, { onConflict: "child_id,date" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-handovers"] });
      toast({ title: "All marked present ✅" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not sign everyone in", description: friendlyError(e), variant: "destructive" });
    },
  });
}


export function useBulkUndoAttendance() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ childIds, date }: { childIds: string[]; date: string }) => {
      const { error } = await supabase
        .from("attendance")
        .delete()
        .in("child_id", childIds)
        .eq("date", date);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance"] });
      toast({ title: "Bulk undo complete ↩" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not undo those sign-ins", description: friendlyError(e), variant: "destructive" });
    },
  });
}
